import { Modal } from '@/components';
import { Column, FormContext, FormField, Row } from '@/components/SettingsLayout';
import { Text, Button, Input, SwitchInput } from '@/ui';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styles from '../styles.module.css';
import { Controller, useFormContext } from 'react-hook-form';
import { EMAIL_VALIDATION_PATTERN } from '@/constants';
import { useState } from 'react';
import {
  useCreateUserAuth0Mutation,
  useDeleteUserMutation,
  useGetUserByIdQuery,
  useUpdateUserMutation,
} from '@/store';
import { skipToken } from '@reduxjs/toolkit/query';
import SkeletonModal from '@/components/SkeletonLoader/SkeletonModal';
import toast from 'react-hot-toast';
import { CompanyTypes, User } from '@/types';
import { useGetUser } from '@/hooks';
import { UserTypes } from '@/enums';
interface EditUserModalProps {
  allowDeletion?: boolean;
}

const EditUserModal = ({ allowDeletion = true }: EditUserModalProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const { company, user } = useGetUser();
  const editForm = searchParams.get('mode') === 'editUser';
  const userId = searchParams.get('userId');
  const addForm = searchParams.get('mode') === 'addUser';
  const isOpen = editForm || addForm;
  const { data, isLoading } = useGetUserByIdQuery(
    userId && id && company?._id
      ? { userId, organizationId: id, companyId: company._id }
      : skipToken,
  );
  const [updateUser] = useUpdateUserMutation();
  const [createUserAuth0] = useCreateUserAuth0Mutation();
  const [deleteUser] = useDeleteUserMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const closeModal = () => {
    navigate(pathname);
  };

  const getUserType = () => {
    const isRoot = company?.type === CompanyTypes.ROOT;
    const isSelfSetting = company?._id.toString() === id;

    if (isRoot && isSelfSetting) {
      return UserTypes.FINAL_USER;
    } else {
      return UserTypes.ORG_USER;
    }
  };

  const submitHandler = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    if (editForm) {
      updateUser({
        userId: userId ?? '',
        body: data,
        organizationId: id || '',
        companyId: company?._id || '',
      })
        .unwrap()
        .then(() => {
          toast.success(t('Accounts.user_updated'));
          closeModal();
        })
        .catch(() => {
          toast.error(t('Accounts.user_update_error'));
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      if (!id) {
        toast.error(t('Accounts.organization_id_missing'));
        setIsSubmitting(false);
        return;
      }
      const newUser: Partial<User> = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        type: getUserType(),
        organizationId: id,
        companies: [id],
      };
      createUserAuth0({
        email: data.email,
        password: data.password,
        body: newUser,
        companyId: company?._id || id,
      })
        .unwrap()
        .then(async () => {
          toast.success(t('Accounts.user_created'));
          closeModal();
        })
        .catch(async () => {
          toast.error(t('Accounts.user_create_error'));
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const onDeleteUser = () => {
    if (!userId || !company || !user) {
      return;
    }

    deleteUser({
      userId,
      companyId: company._id,
      organizationId: user.targetOrganizationId,
    })
      .unwrap()
      .then(() => {
        toast.success(t('Accounts.user_deleted'));
        closeModal();
      })
      .catch(() => toast.error(t('Accounts.user_delete_error')));
  };

  if (isLoading) {
    return (
      <Modal open={isOpen} closeModal={closeModal} fitContent>
        <div className={styles.createOrgModal}>
          <SkeletonModal />
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={isOpen} closeModal={closeModal} fitContent>
      <FormContext
        submitHandler={submitHandler}
        defaultValues={
          editForm && data
            ? {
                firstName: data?.firstName ?? '',
                lastName: data.lastName ?? '',
                email: data.email ?? '',
                phone: data.phone ?? '',
                image: data.image ?? '',
              }
            : {}
        }
        resetContext={true}
      >
        <div className={styles.createOrgModal}>
          <Modal.Title title={editForm ? t('Accounts.edit_user') : t('Dashboard.add_new_user')} />

          <Modal.Content border>
            <Column gapSize='16'>
              <UserForm />
              {/* <Divider />
              <UserPermissions /> */}
            </Column>
          </Modal.Content>
          <Modal.Footer justify={editForm ? 'space-between' : 'flex-end'}>
            <Row justify='justify-between' fullWidth>
              {editForm && (
                <Button
                  variant='danger'
                  onClick={onDeleteUser}
                  type='button'
                  disabled={!allowDeletion}
                >
                  {t('Accounts.delete_user')}
                </Button>
              )}
              <Row gapSize='8' fullWidth={false}>
                <Button
                  variant='outline'
                  onClick={closeModal}
                  type='button'
                  disabled={isSubmitting}
                >
                  {t('Shared.cancel')}
                </Button>
                <Button variant='primary' onClick={() => {}} disabled={isSubmitting}>
                  {t('Accounts.save_changes')}
                </Button>
              </Row>
            </Row>
          </Modal.Footer>
        </div>
      </FormContext>
    </Modal>
  );
};

export default EditUserModal;

const UserForm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const editForm = searchParams.get('mode') === 'editUser';
  const [resetPassword, setResetPassword] = useState(editForm ? false : true);
  const {
    register,
    formState: { errors, isSubmitted },
  } = useFormContext();

  const showResetPassword = () => {
    setResetPassword(true);
  };

  // const onUploadImage = () => {
  //   inputRef.current?.click();
  // };

  // const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (file) {
  //     clearErrors('image');
  //     if (file.size > COMPANY_LOGO_MAX_SIZE) {
  //       setError('image', {
  //         message: t('Settings.logo_size_error'),
  //       });
  //       return;
  //     } else {
  //       const base64File = await convertToBase64(file);
  //       setValue('image', base64File);
  //     }
  //   }
  // };

  return (
    <Column gapSize='12'>
      <FormField className='grid'>
        <label htmlFor={'firstName'}>
          <Text size='s' weight='light-medium'>
            {t(`Form.name`)}
          </Text>
        </label>
        <div className={styles.iconInput}>
          <Input
            id={'firstName'}
            variant='rowInput'
            isError={!!errors.firstName}
            errorMessage={t('Form.required.name')}
            placeholder={t(`Form.placeholders.name`)}
            {...register('firstName', {
              required: t('Form.required.name'),
            })}
            autoFocus={true}
          />
        </div>
      </FormField>
      <FormField className='grid'>
        <label htmlFor={'lastName'}>
          <Text size='s' weight='light-medium'>
            {t(`Form.lastName`)}
          </Text>
        </label>
        <div className={styles.iconInput}>
          <Input
            id={'lastName'}
            variant='rowInput'
            isError={!!errors.lastName}
            errorMessage={t('Form.required.lastName')}
            placeholder={t(`Form.placeholders.lastName`)}
            {...register('lastName', {
              required: t('Form.required.lastName'),
            })}
          />
        </div>
      </FormField>
      <Column>
        <Text size='s' weight='semi-bold'>
          {t('Accounts.login_info')}
        </Text>
        <Text size='xxs' weight='light-medium' color='new-grey'>
          {t('Accounts.login_info_comment')}
        </Text>
      </Column>
      <FormField className='grid'>
        <label htmlFor='email'>
          <Text size='s' weight='light-medium'>
            {t('Form.email')}
          </Text>
        </label>
        <Input
          id='email'
          placeholder={t('Login.emailPlaceholder')}
          autoComplete='off'
          isError={!!errors.email && isSubmitted}
          errorMessage={errors.email?.message as string}
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: EMAIL_VALIDATION_PATTERN,
              message: t('Login.errorEmail'),
            },
          })}
          disabled={editForm}
        />
      </FormField>
      <FormField className='grid'>
        <label htmlFor='password'>
          <Text size='s' weight='light-medium'>
            {t('Form.password')}
          </Text>
        </label>
        {editForm && !resetPassword && (
          <div onClick={showResetPassword} style={{ cursor: 'pointer' }}>
            <Text size='xs' weight='light-medium' color='blue'>
              {t('Login.resetPassword')}
            </Text>
          </div>
        )}
        {resetPassword && (
          <Input
            id='password'
            type='password'
            autoComplete='off'
            isError={!!errors.password && isSubmitted}
            errorMessage={errors.password?.message as string}
            placeholder={t('Form.placeholders.password')}
            {...register('password', {
              required: t('Form.required.password'),
              minLength: {
                value: 8,
                message: t('Form.errors.password'),
              },
            })}
          />
        )}
      </FormField>
    </Column>
  );
};

export const UserPermissions = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  return (
    <Column gapSize='16'>
      <Text size='s' weight='semi-bold'>
        {t('Accounts.permissions')}
      </Text>
      <FormField className='grid'>
        <Text size='s' weight='light-medium'>
          {t('Shared.companies')}
        </Text>
        <Controller
          name={'companies'}
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <>
              <SwitchInput field={field} disabled={false} />
            </>
          )}
        />
      </FormField>
      <FormField className='grid'>
        <Text size='s' weight='light-medium'>
          {t('Navigation.builder')}
        </Text>
        <Controller
          name={'builder'}
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <>
              <SwitchInput field={field} disabled={false} />
            </>
          )}
        />
      </FormField>
      <FormField className='grid'>
        <Text size='s' weight='light-medium'>
          {t('Navigation.settings')}
        </Text>
        <Controller
          name={'settings'}
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <>
              <SwitchInput field={field} disabled={false} />
            </>
          )}
        />
      </FormField>
    </Column>
  );
};
